import React, { useRef } from 'react';
import { Send, Paperclip, Mic, StopCircle } from 'lucide-react';
import AudioWaveVisualizer from './AudioWaveVisualizer';

const ChatInput = ({
  input,
  setInput,
  handleSendMessage,
  handleFileUpload,
  isRecording,
  audioLevel,
  handleStartRecording,
  handleStopRecording,
  fileUploaded,
  placeholder,
}) => {
  const fileInputRef = useRef(null);

  return (
    <div className="w-full max-w-[800px] min-w-0 mt-2.5 px-1.5 sm:px-0">
      <div className="flex items-center gap-0.5 sm:gap-1 min-w-0 overflow-hidden bg-white rounded-2xl p-[5px_6px] sm:p-[5px_10px] border border-gray-200 transition-all duration-300 ease-in-out focus-within:border-primary/40" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
        <AudioWaveVisualizer isRecording={isRecording} audioLevel={audioLevel} />
        
        <input
          type="text"
          className={`flex-1 min-w-0 border-none py-2 px-1.5 sm:py-3 sm:px-3.5 text-sm bg-transparent text-[#333] outline-none focus:outline-none focus:ring-0 focus:shadow-none font-inter placeholder-[#aaa] ${isRecording ? 'text-red-500' : ''}`}
          placeholder={isRecording ? "Listening..." : placeholder || (fileUploaded ? "Ask about your files..." : "Type your message...")}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
          autoFocus
        />

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            className="bg-transparent border-none p-0 text-gray-400 cursor-pointer rounded-full flex items-center justify-center shrink-0 w-8 h-8 sm:w-10 sm:h-10 transition-all duration-200 ease-in-out hover:scale-105 active:scale-95"
            onClick={() => fileInputRef.current?.click()}
            type="button"
            aria-label="Upload file"
            onMouseEnter={e => e.currentTarget.style.color = 'var(--color-primary)'}
            onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              multiple
            />
            <Paperclip size={18} />
          </button>

          <button
            className={`bg-transparent border-none p-0 cursor-pointer rounded-full flex items-center justify-center shrink-0 w-8 h-8 sm:w-10 sm:h-10 transition-all duration-200 ease-in-out hover:scale-105 active:scale-95`}
            style={{ color: isRecording ? '#ef4444' : '#9ca3af' }}
            onClick={isRecording ? handleStopRecording : handleStartRecording}
            aria-label={isRecording ? "Stop recording" : "Start recording"}
            type="button"
            onMouseEnter={e => { if (!isRecording) e.currentTarget.style.color = 'var(--color-primary)'; }}
            onMouseLeave={e => { if (!isRecording) e.currentTarget.style.color = '#9ca3af'; }}
          >
            {isRecording ? <StopCircle size={18} /> : <Mic size={18} />}
          </button>

          <button
            className="flex items-center justify-center shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-xl text-white transition-all duration-200 ease-in-out hover:scale-105 active:scale-95 border-0 cursor-pointer"
            style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
            onClick={handleSendMessage}
            type="button"
            aria-label="Send message"
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.2)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'}
          >
            <Send size={15} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse-0 {
          0%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(1.2); }
        }
        @keyframes pulse-1 {
          0%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(1.5); }
        }
        @keyframes pulse-2 {
          0%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(1.3); }
        }
      `}</style>
    </div>
  );
};

export default ChatInput;